/**
 * Building the WhatsApp link, kept out of the panel so it can be read and tested on its own.
 */

/// The same E.164 shape the backend stores. A phone written before normalization existed does
/// not match, and the panel uses that to explain why it cannot act on the row.
const e164Pattern = /^\+[1-9]\d{7,14}$/;

export function isWhatsAppReachable(phone: string | null | undefined): boolean {
  return Boolean(phone && e164Pattern.test(phone));
}

/**
 * `wa.me` takes the number with no plus sign and the message as `text`.
 *
 * The message is whatever PhoneTec configured, sent verbatim: no name, no request id, no
 * amount is interpolated into it. That is what keeps personal data out of the query string of
 * a service PhoneTec does not control, and it is why the template has no placeholders.
 */
export function buildWhatsAppUrl(phone: string, message: string): string {
  // An empty message opens the chat without one, as the customer sheet does (#229).
  if (!message) {
    return `https://wa.me/${phone.slice(1)}`;
  }

  return `https://wa.me/${phone.slice(1)}?text=${encodeURIComponent(message)}`;
}

/** The message a visitor sends from the landing's repairs section (COR-1). Fixed, with no data. */
export const repairInquiryMessage = 'Hola PhoneTec, quiero consultar por una reparación.';

/**
 * The message a buyer sends PhoneTec about one device (CAT-3). Unlike the panel's message, this
 * one is built here, and only from what the public sheet already shows: brand, model, capacity
 * and the sheet's link. Nothing internal and nothing about the buyer goes into it (BR-033,
 * BR-062).
 */
export function buildDeviceInquiryMessage(
  device: { brand: string; model: string; storageGb: number | null },
  sheetUrl: string,
): string {
  const capacity = device.storageGb === null ? '' : ` de ${device.storageGb} GB`;
  return `Hola PhoneTec, quiero consultar por el ${device.brand} ${device.model}${capacity}: ${sheetUrl}`;
}
